<template>
  <div class="billing-gloss batch-appeals">
    <div class="loading loading-lg mt-2" v-if="loading"></div>
    <div class="cr-panel cr-panel-first" v-else>
      <div class="cr-panel-head">
        <div class="cr-panel-title">
          <fa-icon :icon="['fal', 'gavel']" class="cr-panel-icon"/>
          Recursos do lote
          <span class="bg-count">{{ items.length }}</span>
        </div>
        <div class="cr-panel-right">
          <button class="btn btn-sm btn-gray btn-icon btn-icon-left tooltip tooltip-left"
                  :data-tooltip="newDisabledReason"
                  :disabled="!!newDisabledReason" @click="openAppeal(null)">
            <fa-icon :icon="['fal', 'plus']"/>Novo recurso
          </button>
        </div>
        <div class="cr-panel-subtitle">
          Uma rodada por recurso. A 2ª instância só entra depois da resposta da 1ª.
        </div>
      </div>
      <div class="empty cr-panel-empty" v-if="items.length === 0">
        <div class="empty-icon"><fa-icon :icon="['fal', 'gavel']" size="2x"/></div>
        <p class="empty-subtitle">
          Nenhum recurso neste lote. Selecione itens na aba Glosas e clique em Recursar.
        </p>
      </div>
      <div class="cr-table-scroll" v-else>
        <table class="table table-hover">
          <thead>
          <tr>
            <th>Recurso</th>
            <th>Rodada</th>
            <th>Status</th>
            <th class="text-right">Itens</th>
            <th class="text-right">Recursado</th>
            <th class="text-right">Acatado</th>
            <th class="text-right hide-sm">Mantido</th>
            <th class="hide-sm">Enviado em</th>
            <th class="hide-sm">Respondido em</th>
            <th>Prazo</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="appeal in items" :key="appeal.id"
              :class="{ 'bg-row-dim': appeal.status === 'canceled' }">
            <td class="text-bold">{{ appeal.number }}</td>
            <td><span class="bg-pill bg-pill-outline">{{ roundName(appeal.round) }}</span></td>
            <td>
              <span class="bg-pill" :class="statusClass(appeal.status)">
                {{ statusName(appeal.status) }}
              </span>
            </td>
            <td class="text-right">{{ appeal.itemsCount }}</td>
            <td class="text-right tnum">{{ appeal.totals.appealed | currency }}</td>
            <td class="text-right tnum"
                :class="appeal.totals.accepted ? 'text-green' : 'text-gray'">
              {{ appeal.status === 'answered'
                ? `+${$options.filters.currency(appeal.totals.accepted)}`
                : '—' }}
            </td>
            <td class="text-right tnum hide-sm"
                :class="maintained(appeal) ? 'text-red' : 'text-gray'">
              {{ appeal.status === 'answered'
                ? $options.filters.currency(maintained(appeal))
                : '—' }}
            </td>
            <td class="hide-sm text-nowrap">
              {{ appeal.dates.sentAt ? $options.filters.date(appeal.dates.sentAt) : '—' }}
            </td>
            <td class="hide-sm text-nowrap">
              {{ appeal.dates.answeredAt ? $options.filters.date(appeal.dates.answeredAt) : '—' }}
            </td>
            <td>
              <deadline-clock v-if="appeal.status === 'draft'" :deadline="deadlineOf(appeal)"
                              :show-hint="false"/>
              <span class="text-gray text-xs" v-else-if="appeal.dates.deadlineAt">
                até {{ appeal.dates.deadlineAt | date }}
              </span>
              <span class="text-gray text-xs" v-else>—</span>
            </td>
            <td class="text-right text-nowrap">
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      data-tooltip="Abrir" @click="openAppeal(appeal)">
                <fa-icon :icon="['fal', 'eye']"/>
              </button>
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      :data-tooltip="xmlTooltip(appeal)"
                      :disabled="!appeal.xmlAvailable || appeal.status === 'canceled'"
                      :class="{ loading: downloadingId === appeal.id }"
                      @click="downloadXml(appeal)">
                <fa-icon :icon="['fal', 'cloud-download']"/>
              </button>
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      :data-tooltip="respondTooltip(appeal)"
                      :disabled="appeal.status !== 'sent' || !canManage"
                      @click="respond(appeal)">
                <fa-icon :icon="['fal', 'reply']"/>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <appeal-modal
      v-if="modals.appeal.show"
      :show="modals.appeal.show"
      :batch-id="batchId"
      :appeal-id="modals.appeal.appealId"
      :gloss-ids="[]"
      @close="closeAppeal"
      @respond="respondById"
    />
    <appeal-response-modal
      v-if="modals.response.show"
      :show="modals.response.show"
      :appeal-id="modals.response.appealId"
      @close="closeResponse"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { MANAGE_BILLING_GLOSSES } from '@/data/actions/modules/financial';
import { getName as appealStatusName, getRoundName } from '@/data/billing-appeal-statuses';
import DeadlineClock from './DeadlineClock.vue';
import AppealModal from '../modals/AppealModal.vue';
import AppealResponseModal from '../modals/AppealResponseModal.vue';
import { num, round2 } from '../helpers';

const STATUS_CLASSES = {
  draft: 'bg-pill-gray',
  sent: 'bg-pill-amber',
  answered: 'bg-pill-green',
  canceled: 'bg-pill-gray bg-pill-strike',
};

export default {
  components: {
    DeadlineClock,
    AppealModal,
    AppealResponseModal,
  },
  props: {
    batchId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      downloadingId: null,
      items: [],
      modals: {
        appeal: { show: false, appealId: null },
        response: { show: false, appealId: null },
      },
    };
  },
  computed: {
    canManage() {
      return this.$can(MANAGE_BILLING_GLOSSES);
    },
    pending() {
      return this.items.find(appeal => ['draft', 'sent'].includes(appeal.status)) || null;
    },
    newDisabledReason() {
      if (!this.canManage) return 'Sem permissão';
      if (this.pending && this.pending.status === 'draft') return `Continue o rascunho ${this.pending.number}`;
      if (this.pending) return `Aguardando resposta do ${this.pending.number}`;
      return 'Selecione as glosas na aba Glosas';
    },
  },
  watch: {
    batchId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      return this.$http.get('/billing-appeals', { params: { batchId: this.batchId, limit: 50 } })
        .then(({ data }) => {
          this.items = (data.items || []).sort((a, b) => num(a.round) - num(b.round));
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reload() {
      this.$emit('changed');
      return this.load();
    },
    roundName(round) {
      return getRoundName(round);
    },
    statusName(status) {
      return appealStatusName(status);
    },
    statusClass(status) {
      return STATUS_CLASSES[status] || 'bg-pill-gray';
    },
    maintained(appeal) {
      return round2(num(appeal.totals.appealed) - num(appeal.totals.accepted));
    },
    deadlineOf(appeal) {
      const at = appeal.dates && appeal.dates.deadlineAt;
      if (!at) return { at: null, days: null, configured: false };
      const days = moment(at).startOf('day').diff(moment().startOf('day'), 'days');
      return {
        at, days, configured: true, overdue: days < 0,
      };
    },
    xmlTooltip(appeal) {
      return appeal.xmlAvailable ? 'Baixar XML' : 'Recurso eletrônico só a partir do TISS 4.01.00';
    },
    respondTooltip(appeal) {
      if (!this.canManage) return 'Sem permissão';
      if (appeal.status === 'sent') return 'Registrar resposta';
      if (appeal.status === 'draft') return 'Envie o recurso antes';
      return 'Recurso já respondido';
    },
    openAppeal(appeal) {
      this.modals.appeal = { show: true, appealId: appeal ? appeal.id : null };
    },
    closeAppeal(changed) {
      this.modals.appeal = { show: false, appealId: null };
      if (changed) this.reload();
    },
    respond(appeal) {
      this.respondById(appeal.id);
    },
    respondById(appealId) {
      this.modals.appeal = { show: false, appealId: null };
      this.modals.response = { show: true, appealId };
    },
    closeResponse(saved) {
      this.modals.response = { show: false, appealId: null };
      if (saved) this.reload();
    },
    downloadXml(appeal) {
      this.downloadingId = appeal.id;
      const name = `RECURSO_${appeal.batchNumber || this.batchId}_R${appeal.round}.xml`;
      return this.$file.download(`/billing-appeals/${appeal.id}/xml`, {}, { name })
        .catch(() => {})
        .finally(() => {
          this.downloadingId = null;
        });
    },
  },
};
</script>

<style lang="scss">
@import "../gloss.scss";
</style>
